import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { DataspaceAuditEntryDto, DataspaceUsageEventDto } from '../../models/bautagebuch.models';

type TimelineEntry = {
  id: string;
  title: string;
  actor?: string;
  createdAt?: string;
  kind: 'usage' | 'audit';
  details?: { [key: string]: any };
};

@Component({
  standalone: false,
  selector: 'app-activity-timeline',
  templateUrl: './activity-timeline.component.html',
  styleUrls: ['./activity-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityTimelineComponent {
  @Input() usageEvents: DataspaceUsageEventDto[] = [];
  @Input() auditEntries: DataspaceAuditEntryDto[] = [];

  get entries(): TimelineEntry[] {
    return [
      ...this.usageEvents.map((event) => ({
        id: `usage-${event.id}`,
        title: this.getUsageLabel(event.eventType),
        actor: event.actorOrgName,
        createdAt: event.createdAt,
        kind: 'usage' as const,
        details: event.details
      })),
      ...this.auditEntries.map((entry) => ({
        id: `audit-${entry.id}`,
        title: entry.summary,
        actor: entry.actorOrgName,
        createdAt: entry.createdAt,
        kind: 'audit' as const,
        details: entry.details
      }))
    ].sort((left, right) => (right.createdAt || '').localeCompare(left.createdAt || ''));
  }

  formatTimestamp(value?: string | null): string {
    if (!value) {
      return 'Keine Angabe';
    }

    return new Date(value).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDetails(details: { [key: string]: any }): string[] {
    const labelMap: { [key: string]: string } = {
      reason: 'Begründung',
      summary: 'Zusammenfassung',
      purpose: 'Zweck',
    };
    return Object.entries(details)
      .filter(([, value]) => value !== null && value !== undefined && value !== '')
      .map(([key, value]) => `${labelMap[key] ?? key}: ${value}`);
  }

  private getUsageLabel(eventType: string): string {
    switch ((eventType || '').toUpperCase()) {
      case 'PUBLISHED':
        return 'Bericht freigegeben';
      case 'REQUESTED_ACCESS':
        return 'Anfrage auf Einsicht';
      case 'ACCESS_GRANTED':
        return 'Zugriff freigegeben';
      case 'ACCESS_REJECTED':
        return 'Zugriff abgelehnt';
      case 'VIEWED':
        return 'Bericht geöffnet';
      case 'DOWNLOADED':
        return 'Bericht heruntergeladen';
      case 'ACKNOWLEDGED':
        return 'Durchsicht bestätigt';
      case 'DENIED':
        return 'Aktion blockiert';
      case 'EXPIRED':
        return 'Zugriff abgelaufen';
      case 'REVOKED':
        return 'Freigabe widerrufen';
      case 'SUPERSEDED':
        return 'Neuere Version verfügbar';
      default:
        return eventType || 'Aktivität';
    }
  }
}
